import { Close } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import SimpleMenu from '../../../components/SimpleMenu';
import { showMessage } from '../../../components/utils/showMessage';
import { PostbackContext } from '../context';

import DeleteIcon from '@mui/icons-material/Delete';

const CONDITIONS = ['None', 'Equals', 'Does not contain', 'Contains'];
const AUTH_PROVIDERS = [
  { label: 'Yahoo', value: 'yahoo' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'None', value: 'none' },
];
const COMMON_VALUES = ['{clkid}', '{campaignid}', '{sourceid}', '{conversion}', '{payout}', '{revenue}', '{revenuedate}'];
const FACEBOOK_GRAPH_API_VERSION = 'v21.0';
const buildFacebookDestinationUrl = (pixelId) => `https://graph.facebook.com/${FACEBOOK_GRAPH_API_VERSION}/${pixelId}/events`;
// Facebook destination URLs are auto-built from a Pixel ID rather than typed
// in, so the Pixel ID needs to be recovered from a saved URL to prefill the
// edit form.
const extractFacebookPixelId = (url = '') => url.match(/graph\.facebook\.com\/v[\d.]+\/(\d+)\/events/)?.[1] || '';

const isDeductionRuleValid = ({ parameter, type, value, deductionRate }) => {
  const isBlank = !parameter && !value && !deductionRate;
  return isBlank || Boolean(parameter && type !== 0 && value && deductionRate);
};

export default function EditPostback({ open, handleClose, initialData, onSubmit }) {
  const initialConditions = initialData.conditionRegex ?? [];
  const postbackContext = useContext(PostbackContext);

  const [destination, setDestination] = useState({
    destinationUrl: '',
    trafficSourceId: '',
    deductionRatePerUrl: '',
    authEnabled: false,
    authProvider: 'none',
    pixelId: '',
    accessToken: '',
  });

  const [editingIndex, setEditingIndex] = useState(null);

  const [destinations, setDestinations] = useState([]);

  const [values, setValues] = useState({
    destinationId: '',
    destinationName: '',
    clickIdParam: '',
    deductionRate: '',
    commissionTag: '',
    ignoreDuplicateClickId: false,
  });

  const [conditions, setConditions] = useState([{ type: 0, parameter: '', value: '' }]);

  const [deductionRules, setDeductionRules] = useState([{ type: 1, parameter: '', value: '', deductionRate: '' }]);

  const resetForm = () => {
    setEditingIndex(null);

    setValues({
      destinationId: '',
      destinationName: '',
      destinationUrl: [''],
      trafficSourceId: [''],
      deductionRatePerUrl: [''],
      authProviderPerUrl: [''],
      authEnabledPerUrl: [false],
      clickIdParam: '',
      ignoreDuplicateClickId: false,
      deductionRate: '',
      commissionTag: '',
    });

    setDestination({
      destinationUrl: '',
      trafficSourceId: '',
      deductionRatePerUrl: '',
      authEnabled: false,
      authProvider: '',
      pixelId: '',
      accessToken: '',
    });

    setDestinations([]);

    setConditions([
      {
        type: 0,
        parameter: '',
        value: '',
      },
    ]);

    setDeductionRules([
      {
        type: 1,
        parameter: '',
        value: '',
        deductionRate: '',
      },
    ]);
  };

  const onChange = (e) => {
    const { name, value, checked, type } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleValueChange = (index, key, newValue) => {
    const updatedConditions = [...conditions];

    if (key === 'type' && newValue === 0) {
      updatedConditions[index]['parameter'] = '';
      updatedConditions[index]['value'] = '';
    }

    updatedConditions[index][key] = newValue;
    setConditions(updatedConditions);
  };

  const addCondition = () => {
    setConditions([...conditions, { type: 0, parameter: '', value: '' }]);
  };

  const removeCondition = (index) => {
    const updatedConditions = [...conditions];
    updatedConditions.splice(index, 1);
    setConditions(updatedConditions);
  };

  const handleDeductionRuleChange = (index, key, newValue) => {
    const updatedRules = [...deductionRules];

    if (key === 'type' && newValue === 0) {
      updatedRules[index]['parameter'] = '';
      updatedRules[index]['value'] = '';
      updatedRules[index]['deductionRate'] = '';
    }

    updatedRules[index][key] = newValue;
    setDeductionRules(updatedRules);
  };

  const addDeductionRule = () => {
    setDeductionRules([...deductionRules, { type: 1, parameter: '', value: '', deductionRate: '' }]);
  };

  const removeDeductionRule = (index) => {
    const updatedRules = [...deductionRules];
    updatedRules.splice(index, 1);
    setDeductionRules(updatedRules);
  };

  const preventScrolling = (e) => {
    e.target.addEventListener(
      'wheel',
      function (e) {
        e.preventDefault();
      },
      { passive: false }
    );
  };

  const handleSubmit = async () => {
    try {
      if (!deductionRules.every(isDeductionRuleValid)) {
        showMessage({
          message: 'Each deduction rule needs a Parameter, Condition, Expected Value, and Deduction Rate filled in - or remove the incomplete one.',
          severity: 'error',
        });
        return;
      }

      const filledDeductionRules = deductionRules.filter((rule) => rule.parameter || rule.value || rule.deductionRate);

      await onSubmit({
        destinationId: values.destinationId,
        destinationName: values.destinationName,

        destinationUrl: destinations.map((d) => (d.authProvider === 'facebook' ? buildFacebookDestinationUrl(d.pixelId) : d.destinationUrl)),
        trafficSourceId: destinations.map((d) => d.trafficSourceId),
        deductionRatePerUrl: destinations.map((d) => d.deductionRatePerUrl),
        authProviderPerUrl: destinations.map((d) => d.authProvider),
        authEnabledPerUrl: destinations.map((d) => d.authEnabled),
        accessTokenPerUrl: destinations.map((d) => d.accessToken || ''),

        clickIdParam: values.clickIdParam,
        deductionRate: values.deductionRate,
        commissionTag: values.commissionTag,
        ignoreDuplicateClickId: values.ignoreDuplicateClickId,
        conditionRegex: conditions,
        deductionRules: filledDeductionRules,
      });
      resetForm();
    } catch (error) {
      console.log(error);
    }
  };

  const { traffic } = postbackContext;

  const handleCancel = () => {
    if (initialData) {
      setValues({
        destinationId: initialData.destinationId || '',
        destinationName: initialData.destinationName || '',
        clickIdParam: initialData.clickIdParam || '',
        deductionRate: initialData.deductionRate || '',
        commissionTag: initialData.commissionTag || '',
        ignoreDuplicateClickId: initialData.ignoreDuplicateClickId || false,
      });

      setDestinations(
        (initialData.destinationUrl || []).map((url, index) => {
          const authProvider = initialData.authProviderPerUrl?.[index] || '';

          return {
            destinationUrl: url,
            trafficSourceId: initialData.trafficSourceId?.[index] || '',
            deductionRatePerUrl: initialData.deductionRatePerUrl?.[index] || '',
            authEnabled: initialData.authEnabledPerUrl?.[index] || false,
            authProvider,
            pixelId: authProvider === 'facebook' ? extractFacebookPixelId(url) : '',
            accessToken: initialData.accessTokenPerUrl?.[index] || '',
          };
        })
      );

      setConditions(initialData.conditionRegex?.length ? initialData.conditionRegex : [{ type: 0, parameter: '', value: '' }]);

      setDeductionRules(
        initialData.deductionRules?.length ? initialData.deductionRules : [{ type: 1, parameter: '', value: '', deductionRate: '' }]
      );
    }

    setDestination({
      destinationUrl: '',
      trafficSourceId: '',
      deductionRatePerUrl: '',
      authEnabled: false,
      authProvider: '',
      pixelId: '',
      accessToken: '',
    });

    setEditingIndex(null);

    handleClose();
  };

  const addDestination = () => {
    const isFacebook = destination.authProvider === 'facebook';

    if (isFacebook) {
      if (!destination.pixelId.trim() || !destination.accessToken.trim()) return;
    } else if (!destination.destinationUrl.trim()) {
      return;
    }

    if (editingIndex !== null) {
      setDestinations((prev) => prev.map((item, index) => (index === editingIndex ? { ...destination } : item)));

      setEditingIndex(null);
    } else {
      setDestinations((prev) => [...prev, { ...destination }]);
    }

    setDestination({
      destinationUrl: '',
      trafficSourceId: '',
      deductionRatePerUrl: '',
      authEnabled: false,
      authProvider: '',
      pixelId: '',
      accessToken: '',
    });
  };

  const editDestination = (index) => {
    const selected = destinations[index];

    setDestination({
      destinationUrl: selected.destinationUrl,
      trafficSourceId: selected.trafficSourceId,
      deductionRatePerUrl: selected.deductionRatePerUrl,
      authEnabled: selected.authEnabled,
      authProvider: selected.authProvider,
      pixelId: selected.pixelId || '',
      accessToken: selected.accessToken || '',
    });

    setEditingIndex(index);
  };

  const removeDestination = (index) => {
    setDestinations((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelEdit = () => {
    setEditingIndex(null);

    setDestination({
      destinationUrl: '',
      trafficSourceId: '',
      deductionRatePerUrl: '',
      authEnabled: false,
      authProvider: '',
      pixelId: '',
      accessToken: '',
    });
  };

  useEffect(() => {
    if (!open || !initialData) return;

    setValues({
      destinationId: initialData.destinationId || '',
      destinationName: initialData.destinationName || '',
      clickIdParam: initialData.clickIdParam || '',
      deductionRate: initialData.deductionRate || '',
      commissionTag: initialData.commissionTag || '',
      ignoreDuplicateClickId: initialData.ignoreDuplicateClickId || false,
    });

    setDestinations(
      (initialData.destinationUrl || []).map((url, index) => {
        const authProvider = initialData.authProviderPerUrl?.[index] || '';

        return {
          destinationUrl: url,
          trafficSourceId: initialData.trafficSourceId?.[index] || '',
          deductionRatePerUrl: initialData.deductionRatePerUrl?.[index] || '',
          authEnabled: initialData.authEnabledPerUrl?.[index] || false,
          authProvider,
          pixelId: authProvider === 'facebook' ? extractFacebookPixelId(url) : '',
          accessToken: initialData.accessTokenPerUrl?.[index] || '',
        };
      })
    );

    setConditions(initialData.conditionRegex?.length ? initialData.conditionRegex : [{ type: 0, parameter: '', value: '' }]);

    setDeductionRules(
      initialData.deductionRules?.length ? initialData.deductionRules : [{ type: 1, parameter: '', value: '', deductionRate: '' }]
    );

    setDestination({
      destinationUrl: '',
      trafficSourceId: '',
      deductionRatePerUrl: '',
      authEnabled: false,
      authProvider: '',
      pixelId: '',
      accessToken: '',
    });

    setEditingIndex(null);
  }, [open, initialData]);

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="lg">
      <DialogTitle>
        Edit Postback
        <IconButton aria-label="close" onClick={handleClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              bgcolor: 'grey.50',
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Basic Information
            </Typography>

            <Box
              display="grid"
              gridTemplateColumns={{
                xs: '1fr',
                md: '1fr 1fr',
              }}
              gap={2}
            >
              <TextField size="small" name="destinationId" label="Destination ID" value={values.destinationId} onChange={onChange} fullWidth />

              <TextField size="small" name="destinationName" label="Destination Name" value={values.destinationName} onChange={onChange} fullWidth />
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Destination Configuration
            </Typography>

            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr 1fr' }} gap={2}>
              {destination.authProvider === 'facebook' ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    size="small"
                    label="Facebook Pixel ID"
                    value={destination.pixelId}
                    onChange={(e) =>
                      setDestination((prev) => ({
                        ...prev,
                        pixelId: e.target.value,
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Facebook Access Token"
                    value={destination.accessToken}
                    onChange={(e) =>
                      setDestination((prev) => ({
                        ...prev,
                        accessToken: e.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Box>
              ) : (
                <TextField
                  size="small"
                  label="Destination URL"
                  name="destinationUrl"
                  value={destination.destinationUrl}
                  onChange={(e) =>
                    setDestination((prev) => ({
                      ...prev,
                      destinationUrl: e.target.value,
                    }))
                  }
                  fullWidth
                />
              )}

              <TextField
                size="small"
                select
                label="Traffic Source"
                value={destination.trafficSourceId}
                onChange={(e) =>
                  setDestination((prev) => ({
                    ...prev,
                    trafficSourceId: e.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="">None</MenuItem>

                {traffic.map((item) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Deduction Rate (%)"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={destination.deductionRatePerUrl}
                onChange={(e) =>
                  setDestination((prev) => ({
                    ...prev,
                    deductionRatePerUrl: e.target.value,
                  }))
                }
                fullWidth
              />
            </Box>

            {destination.authProvider !== 'facebook' && (
              <>
                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  URL Placeholders
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Click a placeholder below to insert it into the destination URL.
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1}>
                  {COMMON_VALUES.map((value) => (
                    <Button
                      key={value}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'none' }}
                      onClick={() =>
                        setDestination((prev) => {
                          const url = prev.destinationUrl;

                          return {
                            ...prev,
                            destinationUrl: url + value,
                          };
                        })
                      }
                    >
                      {value}
                    </Button>
                  ))}
                </Box>
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Authentication
            </Typography>

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={destination.authEnabled}
                      onChange={(e) =>
                        setDestination((prev) => ({
                          ...prev,
                          authEnabled: e.target.checked,
                          authProvider: e.target.checked ? prev.authProvider || 'none' : 'none',
                        }))
                      }
                    />
                  }
                  label="Authenticated"
                />

                <TextField
                  select
                  size="small"
                  label="Provider"
                  value={destination.authProvider}
                  disabled={!destination.authEnabled}
                  onChange={(e) =>
                    setDestination((prev) => ({
                      ...prev,
                      authProvider: e.target.value,
                    }))
                  }
                  sx={{ minWidth: 200 }}
                >
                  {AUTH_PROVIDERS.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>
                      {provider.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                {editingIndex !== null && (
                  <Button variant="outlined" onClick={cancelEdit} sx={{ mr: 1 }}>
                    Cancel
                  </Button>
                )}

                <Button variant="contained" onClick={addDestination}>
                  {editingIndex !== null ? 'Update Destination' : 'Add Destination'}
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mt: 3,
              borderRadius: 2,
              bgcolor: 'grey.50',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Added Destinations
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {destinations.length} {destinations.length === 1 ? 'destination' : 'destinations'}
              </Typography>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 320 }}>Destination URL</TableCell>
                    <TableCell>Traffic Source</TableCell>
                    <TableCell>Deduction Rate %</TableCell>
                    <TableCell>Authenticated</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell align="center" width={100}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {destinations.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{
                          py: 5,
                          color: 'text.secondary',
                        }}
                      >
                        No destinations have been added yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    destinations.map((item, index) => {
                      const displayUrl = item.authProvider === 'facebook' ? `Pixel ID: ${item.pixelId}` : item.destinationUrl;

                      return (
                      <TableRow hover key={item.destinationUrl || item.pixelId || index}>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Tooltip title={displayUrl} arrow placement="top-start">
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                cursor: 'pointer',
                              }}
                            >
                              {displayUrl}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>{traffic.find((t) => t._id === item.trafficSourceId)?.name || '-'}</TableCell>

                        <TableCell>{item.deductionRatePerUrl || '-'}</TableCell>

                        <TableCell>{item.authEnabled ? 'Yes' : 'No'}</TableCell>

                        <TableCell>{item.authProvider && item.authProvider !== 'none' ? item.authProvider : '-'}</TableCell>

                        <TableCell align="center">
                          <IconButton color="primary" size="small" onClick={() => editDestination(index)}>
                            <EditIcon fontSize="small" />
                          </IconButton>

                          <IconButton color="error" size="small" onClick={() => removeDestination(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr',
              lg: '360px minmax(0, 1fr)',
            }}
            gap={3}
            alignItems="start"
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'grey.50',
                height: '100%',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Postback Settings
              </Typography>

              <Box display="grid" gridTemplateColumns="1fr" gap={2}>
                <TextField size="small" name="clickIdParam" label="Parameter For Click ID" value={values.clickIdParam} onChange={onChange} fullWidth />

                <TextField
                  size="small"
                  name="deductionRate"
                  label="Deduction Rate (%)"
                  type="number"
                  value={values.deductionRate}
                  onChange={(e) => {
                    const parseInput = parseInt(e.target.value);
                    e.target.value = parseInput > 0 && parseInput <= 100 ? parseInput : '';
                    onChange(e);
                  }}
                  onFocus={preventScrolling}
                  fullWidth
                />

                <TextField size="small" name="commissionTag" label="Parameter For Commission" value={values.commissionTag} onChange={onChange} fullWidth />

                <FormControlLabel
                  control={<Checkbox name="ignoreDuplicateClickId" checked={values.ignoreDuplicateClickId} onChange={onChange} />}
                  label="Ignore Duplicate Click ID"
                />
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'grey.50',
                height: '100%',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Optional Conditions
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {conditions.length}/3
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                Conditions are <strong>case-sensitive</strong>. Leave this section empty to always send the postback.
              </Alert>

              <Box display="flex" flexDirection="column" gap={2}>
                {conditions.map((condition, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Condition {index + 1}
                    </Typography>

                    <Box
                      display="grid"
                      gridTemplateColumns={{
                        xs: '1fr',
                        md: '170px 180px 1fr auto',
                      }}
                      gap={2}
                      alignItems="center"
                    >
                      <TextField
                        size="small"
                        label="Parameter"
                        value={condition.parameter}
                        onChange={(e) => handleValueChange(index, 'parameter', e.target.value)}
                        disabled={condition.type === 0}
                        placeholder="country"
                      />

                      <SimpleMenu item={condition} menuItems={CONDITIONS} index={index} handleValueChange={handleValueChange} />

                      <TextField
                        size="small"
                        label="Expected Value"
                        value={condition.value}
                        onChange={(e) => handleValueChange(index, 'value', e.target.value)}
                        disabled={condition.type === 0}
                        placeholder="PH"
                      />

                      {conditions.length > 1 && (
                        <IconButton color="error" onClick={() => removeCondition(index)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}

                {conditions.length < 3 && (
                  <Button variant="outlined" onClick={addCondition} sx={{ alignSelf: 'flex-start' }}>
                    + Add Another Condition
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Category-Based Deduction Rates
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Apply a different deduction rate based on any parameter this postback receives (e.g. <strong>category</strong> Equals <strong>Flight</strong> → <strong>8%</strong>). Matching is <strong>case-insensitive</strong> (Flight, flight, and FLIGHT all match). Rules are checked in order and the first match wins. If none match, the flat Deduction Rate above still applies.
            </Alert>

            <Box display="flex" flexDirection="column" gap={2}>
              {deductionRules.map((rule, index) => (
                <Paper
                  key={index}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Rule {index + 1}
                  </Typography>

                  <Box
                    display="grid"
                    gridTemplateColumns={{
                      xs: '1fr',
                      md: '170px 180px 1fr 160px auto',
                    }}
                    gap={2}
                    alignItems="center"
                  >
                    <TextField
                      size="small"
                      label="Parameter"
                      value={rule.parameter}
                      onChange={(e) => handleDeductionRuleChange(index, 'parameter', e.target.value)}
                      placeholder="category"
                    />

                    <SimpleMenu item={rule} menuItems={CONDITIONS} index={index} handleValueChange={handleDeductionRuleChange} />

                    <TextField
                      size="small"
                      label="Expected Value"
                      value={rule.value}
                      onChange={(e) => handleDeductionRuleChange(index, 'value', e.target.value)}
                      placeholder="Flight"
                    />

                    <TextField
                      size="small"
                      label="Deduction Rate (%)"
                      type="number"
                      inputProps={{ min: 0, max: 100 }}
                      value={rule.deductionRate}
                      onChange={(e) => handleDeductionRuleChange(index, 'deductionRate', e.target.value)}
                    />

                    {deductionRules.length > 1 && (
                      <IconButton color="error" onClick={() => removeDeductionRule(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Paper>
              ))}

              <Button variant="outlined" onClick={addDeductionRule} sx={{ alignSelf: 'flex-start' }}>
                + Add Another Rule
              </Button>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancel} color="secondary" variant="contained">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
